import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';

import {
	Avatar,
	Button,
	Container,
	Jumbo,
	Pagination,
	Separator,
	Skeleton,
	Table,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

const Background = styled(Box)`
	min-height: 100vh;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const StyledLink = styled(NavLink)`
	${({ theme }) => css`
		color: ${theme.colors.secondary};
		text-decoration: underline;

		&:hover {
			text-decoration: none;
		}
	`}
`;

export default function WorkspacesSettings() {
	const { t } = useTranslation();

	const [pagination, setPagination] = useState<Pagination>({ first: 10 });

	const query = graphql.useUserRolesQuery({
		variables: pagination,
	});

	const [revoke] = graphql.useRevokeUserRoleMutation();

	const revokeRole = async ({ source, role, target: { targetID, type } }: graphql.UserRole) => {
		const input: graphql.RevokeRoleInput = {
			userID: source.id,
			roleID: role.id,
			targetID: targetID,
			targetType: type,
		};

		await revoke({ variables: { input: input } });
		await query.refetch();
	};

	return (
		<>
			<Jumbo title={t('settings.roles.title')} description={t('settings.roles.description')} />
			<Separator />
			<Background>
				<Container py={4}>
					<Table>
						<Table.Header>
							<Table.Cell basis={0.2}>{t('settings.roles.table.header.user')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.roles.table.header.type')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.roles.table.header.target')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.roles.table.header.hris')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.roles.table.header.actions')}</Table.Cell>
						</Table.Header>
						{query.data ? (
							query.data.userRoles.edges.map(({ node }) => {
								return (
									<Table.Row key={node.id}>
										<Table.Cell basis={0.2}>
											<Flex alignItems="center">
												<Box mr={2} flexGrow={0} flexShrink={0}>
													<Avatar user={node.source as graphql.User} size={24} />
												</Box>
												<StyledLink to={`employees/${node.source.id}`}>
													{node.source.firstName} {node.source.lastName}
												</StyledLink>
											</Flex>
										</Table.Cell>
										<Table.Cell basis={0.2}>
											{t(`roles.${node.role.id}`)}
											{node.isIndirect && '*'}
										</Table.Cell>
										<Table.Cell basis={0.2}>
											{node.targetV2?.__typename === 'User' && (
												<Flex alignItems="center">
													<Box mr={2} flexGrow={0} flexShrink={0}>
														<Avatar user={node.targetV2 as graphql.User} size={24} />
													</Box>

													<StyledLink to={`employees/${node.targetV2.id}`}>
														{node.targetV2.firstName} {node.targetV2.lastName}
													</StyledLink>
												</Flex>
											)}
											{node.targetV2?.__typename === 'Group' && node.targetV2.name}
										</Table.Cell>
										<Table.Cell basis={0.2}>{node.externalReferenceID}</Table.Cell>

										{/* Row actions */}
										<Table.Cell basis={0.2}>
											{/* Delete management role */}
											<Button
												small
												type="destructive"
												title={t('common.archive')}
												onClick={async () => await revokeRole(node as graphql.UserRole)}
											/>
										</Table.Cell>
									</Table.Row>
								);
							})
						) : (
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.2} />
								</Table.Row>
							</Skeleton.Repeat>
						)}
					</Table>
					<Table.Paging
						pageInfo={query.data?.userRoles.pageInfo}
						onPaginate={(pagination) => setPagination(pagination)}
					/>
				</Container>
			</Background>
		</>
	);
}
